import type { Transaction, CategorizationResult, CategorizationContext } from '../types';
import type { BaseCategorizer } from './BaseCategorizer.js';

/**
 * Configuration for the categorization pipeline
 */
export type PipelineConfig = {
  minConfidence?: number;
  enableBatching?: boolean;
  batchSize?: number;
};

/**
 * Progress information for batch categorization
 */
export type ProgressInfo = {
  processed: number;
  total: number;
  percentage: number;
};

/**
 * Categorization Pipeline State
 * Manages categorizers and configuration
 */
export type PipelineState = {
  categorizers: BaseCategorizer[];
  config: Required<PipelineConfig>;
};

/**
 * Create a new categorization pipeline
 * @param config - Pipeline configuration
 * @returns Pipeline state
 */
export function createPipeline(config: PipelineConfig = {}): PipelineState {
  return {
    categorizers: [],
    config: {
      minConfidence: config.minConfidence || 0.5,
      enableBatching: config.enableBatching !== false,
      batchSize: config.batchSize || 10
    }
  };
}

/**
 * Register a categorizer
 * @param state - Pipeline state
 * @param categorizer - Categorizer to register
 */
export function registerCategorizer(state: PipelineState, categorizer: BaseCategorizer): void {
  if (!categorizer.categorize) {
    throw new Error('Categorizer must implement categorize() method');
  }

  state.categorizers.push(categorizer);

  // Sort by priority (lower number = higher priority)
  state.categorizers.sort((a, b) => a.priority - b.priority);

  console.log(`[CategorizationPipeline] Registered: ${categorizer.name} (priority: ${categorizer.priority})`);
}

/**
 * Check if a transaction should skip categorization
 */
function shouldSkipCategorization(transaction: Transaction): boolean {
  return transaction.categorySource === 'user';
}

/**
 * Get best categorization result from all categorizers
 */
async function getBestResult(
  state: PipelineState,
  transaction: Transaction,
  context: CategorizationContext
): Promise<CategorizationResult> {
  let bestResult: CategorizationResult = {
    category: 'Uncategorized',
    confidence: 0.0,
    method: 'none'
  };

  // Try each categorizer in priority order
  for (const categorizer of state.categorizers) {
    // Skip if categorizer can't handle this transaction
    if (!categorizer.canCategorize(transaction, context)) {
      continue;
    }

    try {
      const result = await categorizer.categorize(transaction, context);

      if (result && result.confidence > bestResult.confidence) {
        bestResult = result;

        // If we've exceeded the minimum confidence threshold, we can stop
        if (result.confidence >= state.config.minConfidence) {
          //TODO: add debug logging
          // console.log(`[Pipeline] ${categorizer.name} categorized as "${result.category}" (confidence: ${result.confidence})`);
          break;
        }
      }
    } catch (error) {
      console.error(`[Pipeline] Error in ${categorizer.name}:`, error);
    }
  }

  return bestResult;
}

/**
 * Apply categorization result to transaction
 */
function applyResult(
  transaction: Transaction,
  result: CategorizationResult
): Transaction {
  return {
    ...transaction,
    category: result.category,
    confidence: result.confidence,
    categoryMethod: result.method,
    person: result.person || transaction.person,
    displayName: result.displayName !== undefined ? result.displayName : transaction.displayName,
    isRecurring: result.isRecurring !== undefined ? result.isRecurring : transaction.isRecurring,
    recurringType: result.recurringType !== undefined ? result.recurringType : transaction.recurringType,
    excluded: result.excluded !== undefined ? result.excluded : transaction.excluded,
    exclusionReason: result.exclusionReason !== undefined ? result.exclusionReason : transaction.exclusionReason,
    excludedBy: result.excludedBy !== undefined ? result.excludedBy : transaction.excludedBy,
    excludedAt: result.excludedAt !== undefined ? result.excludedAt : transaction.excludedAt
  };
}

/**
 * Categorize a single transaction
 * @param state - Pipeline state
 * @param transaction - Transaction to categorize
 * @param context - Categorization context
 * @returns Categorized transaction
 */
export async function categorize(
  state: PipelineState,
  transaction: Transaction,
  context: CategorizationContext = {}
): Promise<Transaction> {
  // Skip if already manually categorized by user
  if (shouldSkipCategorization(transaction)) {
    return {
      ...transaction,
      confidence: 1.0,
      categoryMethod: 'user'
    };
  }

  const result = await getBestResult(state, transaction, context);
  return applyResult(transaction, result);
}

/**
 * Categorize multiple transactions
 * @param state - Pipeline state
 * @param transactions - Transactions to categorize
 * @param context - Categorization context
 * @returns Categorized transactions
 */
export async function categorizeMany(
  state: PipelineState,
  transactions: Transaction[],
  context: CategorizationContext = {}
): Promise<Transaction[]> {
  const results: Transaction[] = [];

  for (const transaction of transactions) {
    const result = await categorize(state, transaction, context);
    results.push(result);
  }

  return results;
}

/**
 * Categorize in batches (for large datasets)
 * @param state - Pipeline state
 * @param transactions - Transactions to categorize
 * @param context - Categorization context
 * @param progressCallback - Progress callback
 * @returns Categorized transactions
 */
export async function categorizeBatch(
  state: PipelineState,
  transactions: Transaction[],
  context: CategorizationContext = {},
  progressCallback: ((progress: ProgressInfo) => void) | null = null
): Promise<Transaction[]> {
  const results: Transaction[] = [];
  const batchSize = state.config.batchSize;

  for (let i = 0; i < transactions.length; i += batchSize) {
    const batch = transactions.slice(i, i + batchSize);
    const batchResults = await categorizeMany(state, batch, context);
    results.push(...batchResults);

    if (progressCallback) {
      progressCallback({
        processed: results.length,
        total: transactions.length,
        percentage: (results.length / transactions.length) * 100
      });
    }
  }

  return results;
}

/**
 * Get list of registered categorizers
 * @param state - Pipeline state
 * @returns Array of categorizer info
 */
export function getRegisteredCategorizers(state: PipelineState) {
  return state.categorizers.map(c => c.getInfo());
}

/**
 * Update pipeline configuration
 * @param state - Pipeline state
 * @param config - Configuration to update
 */
export function updateConfig(state: PipelineState, config: Partial<PipelineConfig>): void {
  state.config = { ...state.config, ...config } as Required<PipelineConfig>;
}

/**
 * Get current configuration
 * @param state - Pipeline state
 * @returns Current configuration
 */
export function getConfig(state: PipelineState): Required<PipelineConfig> {
  return { ...state.config };
}

/**
 * Get categorizer by name
 * @param state - Pipeline state
 * @param name - Categorizer name
 * @returns Categorizer or null
 */
export function getCategorizer(state: PipelineState, name: string): BaseCategorizer | null {
  return state.categorizers.find(c => c.name === name) || null;
}

/**
 * Get count of registered categorizers
 * @param state - Pipeline state
 * @returns Number of categorizers
 */
export function getCategorizerCount(state: PipelineState): number {
  return state.categorizers.length;
}

/**
 * Remove a categorizer by name
 * @param state - Pipeline state
 * @param name - Categorizer name to remove
 * @returns true if removed, false otherwise
 */
export function removeCategorizer(state: PipelineState, name: string): boolean {
  const index = state.categorizers.findIndex(c => c.name === name);
  if (index !== -1) {
    state.categorizers.splice(index, 1);
    console.log(`[CategorizationPipeline] Removed: ${name}`);
    return true;
  }
  return false;
}

/**
 * Clear all categorizers
 * @param state - Pipeline state
 */
export function clearCategorizers(state: PipelineState): void {
  state.categorizers = [];
  console.log('[CategorizationPipeline] Cleared all categorizers');
}
